import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Col, Modal, Row } from 'antd';
import { Formik } from 'formik';
import { Form, SubmitButton, Switch } from 'formik-antd';
import * as Yup from 'yup';

import { BaseModal } from 'components/wrappers/BaseModal';
import FormItem from 'components/formik/FormItem';
import { BaseModalFooter } from 'components/wrappers/BaseModal';
import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  configureFinancialReportColumns,
  getFinancialReportsSettings,
} from 'redux/reducers/homeDashboardSlice';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import {
  FinancialReportSortFieldEnum,
  FinancialFilterViewModel,
} from 'api/backofficeApiClient';

import styles from './ProfitReportSettingsModal.module.less';

const validationSchema = Yup.object({
  isMember1Name: Yup.boolean(),
  isMember2Name: Yup.boolean(),
  isWorkerTypeOrWorkerId: Yup.boolean(),
  isWorkerJobTitle: Yup.boolean(),
  isCommissionDetails: Yup.boolean(),
  isDate: Yup.boolean(),
  isMemberMarkup: Yup.boolean(),
  isBillHoursReg: Yup.boolean(),
  isBillHoursOt: Yup.boolean(),
  isBillRateReg: Yup.boolean(),
  isBillRateOt: Yup.boolean(),
  isBillAmount: Yup.boolean(),
  isPayHoursReg: Yup.boolean(),
  isPayHoursOt: Yup.boolean(),
  isPayRateReg: Yup.boolean(),
  isPayRateOt: Yup.boolean(),
  isPayAmount: Yup.boolean(),
  isMBPRate: Yup.boolean(),
  isMBPFee: Yup.boolean(),
  isMSPFee: Yup.boolean(),
  isVMSFee: Yup.boolean(),
  isTotalProfit: Yup.boolean(),
  isMember1USDHr: Yup.boolean(),
  isMember2USDHr: Yup.boolean(),
  isMember1ProfitPercent: Yup.boolean(),
  isMember2ProfitPercent: Yup.boolean(),
  isMember1Profit: Yup.boolean(),
  isMember2Profit: Yup.boolean(),
  isInvoiceNumber: Yup.boolean(),
});

export const mapColumnSettingsToBooleanValues = (
  data: FinancialFilterViewModel,
) => {
  return {
    [FinancialReportSortFieldEnum.CustomerName]: true,
    [FinancialReportSortFieldEnum.Member1Name]: data.isMember1Name,
    [FinancialReportSortFieldEnum.Member2Name]: data.isMember2Name,
    [FinancialReportSortFieldEnum.WorkerTypeWorkerID]:
      data.isWorkerTypeOrWorkerId,
    [FinancialReportSortFieldEnum.ParentVendor]: data.isParentVendor,
    [FinancialReportSortFieldEnum.WorkerName]: data.isWorkerName,
    [FinancialReportSortFieldEnum.WorkerJobTitle]: data.isWorkerJobTitle,
    [FinancialReportSortFieldEnum.CommissionDetails]: data.isCommissionDetails,
    [FinancialReportSortFieldEnum.Date]: data.isDate,
    [FinancialReportSortFieldEnum.MemberMarkup]: data.isMemberMarkup,
    [FinancialReportSortFieldEnum.BillHoursReg]: data.isBillHoursReg,
    [FinancialReportSortFieldEnum.BillHoursOT]: data.isBillHoursOt,
    [FinancialReportSortFieldEnum.BillRateReg]: data.isBillRateReg,
    [FinancialReportSortFieldEnum.BillRateOT]: data.isBillRateOt,
    [FinancialReportSortFieldEnum.BillAmount]: data.isBillAmount,
    [FinancialReportSortFieldEnum.PayHoursReg]: data.isPayHoursReg,
    [FinancialReportSortFieldEnum.PayHoursOT]: data.isPayHoursOt,
    [FinancialReportSortFieldEnum.PayRateReg]: data.isPayRateReg,
    [FinancialReportSortFieldEnum.PayRateOT]: data.isPayRateOt,
    [FinancialReportSortFieldEnum.PayAmount]: data.isPayAmount,
    [FinancialReportSortFieldEnum.MBPRate]: data.isMBPRate,
    [FinancialReportSortFieldEnum.MBPFee]: data.isMBPFee,
    [FinancialReportSortFieldEnum.MSPFee]: data.isMSPFee,
    [FinancialReportSortFieldEnum.VMSFee]: data.isVMSFee,
    [FinancialReportSortFieldEnum.TotalProfit]: data.isTotalProfit,
    [FinancialReportSortFieldEnum.Member1USDHr]: data.isMember1USDHr,
    [FinancialReportSortFieldEnum.Member2USDHr]: data.isMember2USDHr,
    [FinancialReportSortFieldEnum.Member1ProfitPercent]:
      data.isMember1ProfitPercent,
    [FinancialReportSortFieldEnum.Member2ProfitPercent]:
      data.isMember2ProfitPercent,
    [FinancialReportSortFieldEnum.Member1Profit]: data.isMember1Profit,
    [FinancialReportSortFieldEnum.Member2Profit]: data.isMember2Profit,
    [FinancialReportSortFieldEnum.InvoiceNumber]: data.isInvoiceNumber,
  };
};

type Props = {
  onSuccess: (data: FinancialFilterViewModel) => void;
  onClose: () => void;
};

function getInitialValues(): FinancialFilterViewModel {
  return {
    isMember1Name: false,
    isMember2Name: false,
    isWorkerTypeOrWorkerId: false,
    isParentVendor: false,
    isWorkerName: false,
    isWorkerJobTitle: false,
    isCommissionDetails: false,
    isDate: false,
    isMemberMarkup: false,
    isBillHoursReg: false,
    isBillHoursOt: false,
    isBillRateReg: false,
    isBillRateOt: false,
    isBillAmount: false,
    isPayHoursReg: false,
    isPayHoursOt: false,
    isPayRateReg: false,
    isPayRateOt: false,
    isPayAmount: false,
    isMBPRate: false,
    isMBPFee: false,
    isMSPFee: false,
    isVMSFee: false,
    isTotalProfit: false,
    isMember1USDHr: false,
    isMember2USDHr: false,
    isMember1ProfitPercent: false,
    isMember2ProfitPercent: false,
    isMember1Profit: false,
    isMember2Profit: false,
    isInvoiceNumber: false,
  };
}

export default function ProfitReportSettingsModal(props: Props) {
  const { onClose, onSuccess } = props;

  const [columnsSettings, setColumnsSettings] =
    useState<FinancialFilterViewModel>(getInitialValues());
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();
  const formRef: any = useRef();

  const handleSubmit = async (data: FinancialFilterViewModel) => {
    const result = await dispatch(configureFinancialReportColumns(data));

    if (configureFinancialReportColumns.fulfilled.match(result)) {
      onSuccess(data);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const loadColumnsSettings = useCallback(async () => {
    setIsLoading(true);

    const result = await dispatch(getFinancialReportsSettings());

    if (getFinancialReportsSettings.fulfilled.match(result)) {
      setColumnsSettings(result.payload);
    }

    setIsLoading(false);
  }, [dispatch]);

  useEffect(() => {
    loadColumnsSettings();
  }, [loadColumnsSettings]);

  const handleCloseModal = () => {
    if (formRef.current.isSubmitting) return;

    if (formRef.current.dirty) {
      return Modal.confirm({
        title: 'Changes won’t be saved. Are you sure?',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  return (
    <BaseModal
      title="Columns to Display"
      maxWidth={888}
      open={true}
      closable={true}
      onCancel={handleCloseModal}
      className={styles.profitModal}
    >
      <Formik
        initialValues={columnsSettings}
        innerRef={formRef}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form layout="vertical" className={styles.profitForm}>
          <div className="modal-scroll-wrapper">
            <Row gutter={40}>
              <Col xs={24} sm={8}>
                <FormItem name="isMember1Name">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isMember1Name"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Member 1 Name</div>
                  </div>
                </FormItem>
                <FormItem name="isMember2Name">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isMember2Name"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Member 2 Name</div>
                  </div>
                </FormItem>
                <FormItem name="isWorkerTypeOrWorkerId">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isWorkerTypeOrWorkerId"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>
                      Worker Type / Worker ID
                    </div>
                  </div>
                </FormItem>
                <FormItem name="isParentVendor">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isParentVendor"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Parent Vendor</div>
                  </div>
                </FormItem>
                <FormItem name="isWorkerName">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isWorkerName"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Worker Name</div>
                  </div>
                </FormItem>
                <FormItem name="isWorkerJobTitle">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isWorkerJobTitle"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Worker Job Title</div>
                  </div>
                </FormItem>
                <FormItem name="isCommissionDetails">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isCommissionDetails"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Commission Details</div>
                  </div>
                </FormItem>
                <FormItem name="isDate">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isDate"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Date</div>
                  </div>
                </FormItem>
                <FormItem name="isMemberMarkup">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isMemberMarkup"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Member Markup</div>
                  </div>
                </FormItem>
                <FormItem name="isBillHoursReg">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isBillHoursReg"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Bill Hours (Reg)</div>
                  </div>
                </FormItem>
                <FormItem name="isBillHoursOt">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isBillHoursOt"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Bill Hours (OT)</div>
                  </div>
                </FormItem>
              </Col>
              <Col xs={24} sm={8}>
                <FormItem name="isBillRateReg">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isBillRateReg"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Bill Rate (Reg)</div>
                  </div>
                </FormItem>
                <FormItem name="isBillRateOt">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isBillRateOt"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Bill Rate (OT)</div>
                  </div>
                </FormItem>
                <FormItem name="isBillAmount">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isBillAmount"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Bill Amount</div>
                  </div>
                </FormItem>
                <FormItem name="isPayHoursReg">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isPayHoursReg"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Pay Hours (Reg)</div>
                  </div>
                </FormItem>
                <FormItem name="isPayHoursOt">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isPayHoursOt"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Pay Hours (OT)</div>
                  </div>
                </FormItem>
                <FormItem name="isPayRateReg">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isPayRateReg"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Pay Rate (Reg)</div>
                  </div>
                </FormItem>
                <FormItem name="isPayRateOt">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isPayRateOt"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Pay Rate (OT)</div>
                  </div>
                </FormItem>
                <FormItem name="isPayAmount">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isPayAmount"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Pay Amount</div>
                  </div>
                </FormItem>
                <FormItem name="isMBPRate">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isMBPRate"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>MBP Rate</div>
                  </div>
                </FormItem>
                <FormItem name="isMBPFee">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isMBPFee"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>MBP Fee ($)</div>
                  </div>
                </FormItem>
                <FormItem name="isMSPFee">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isMSPFee"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>MSP Fee</div>
                  </div>
                </FormItem>
              </Col>
              <Col xs={24} sm={8}>
                <FormItem name="isVMSFee">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isVMSFee"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>VMS Fee</div>
                  </div>
                </FormItem>
                <FormItem name="isTotalProfit">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isTotalProfit"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Total Profit</div>
                  </div>
                </FormItem>
                <FormItem name="isMember1USDHr">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isMember1USDHr"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Member 1 USD/Hr</div>
                  </div>
                </FormItem>
                <FormItem name="isMember2USDHr">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isMember2USDHr"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Member 2 USD/Hr</div>
                  </div>
                </FormItem>
                <FormItem name="isMember1ProfitPercent">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isMember1ProfitPercent"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Member 1 Profit %</div>
                  </div>
                </FormItem>
                <FormItem name="isMember2ProfitPercent">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isMember2ProfitPercent"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Member 2 Profit %</div>
                  </div>
                </FormItem>
                <FormItem name="isMember1Profit">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isMember1Profit"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Member 1 Profit</div>
                  </div>
                </FormItem>
                <FormItem name="isMember2Profit">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isMember2Profit"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Member 2 Profit</div>
                  </div>
                </FormItem>
                <FormItem name="isInvoiceNumber">
                  <div className={styles.switch}>
                    <div className={styles.switchBox}>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        name="isInvoiceNumber"
                        loading={isLoading}
                      />
                    </div>
                    <div className={styles.switchText}>Invoice Number</div>
                  </div>
                </FormItem>
              </Col>
            </Row>
          </div>
          <BaseModalFooter>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <SubmitButton>Save</SubmitButton>
          </BaseModalFooter>
        </Form>
      </Formik>
    </BaseModal>
  );
}
